import * as React from "react";
import { forwardRef, useState } from "react";
import { Dialog, DialogContent, IconButton, styled } from "@mui/material";
import {
  ConfigMetaData,
  ValueWithSource,
} from "@kahunmedical/common-configuration";
import { KahunSettingValue } from "./kahunSettingValue";
import { SettingsButton } from "./controls/settingsButton";
import clsx from "clsx";
import { SettingSourceIcon } from "./settingSourceIcon";
import LinkIcon from "@mui/icons-material/Link";

//region [[ Styles ]]

export const KahunSettingPresentationView = styled("div")(({ theme }) => ({
  display: "flex",
  padding: "19px",
  paddingBottom: "5px",
  flexFlow: "row",
  justifyContent: "space-between",
  alignItems: "flex-start",
  flex: "1 1 calc(50% - 26px)",
  flexWrap: "wrap",
  gap: 10,
  "&.selected": {
    background: "rgb(255 255 224 / 40%)",
  },
  "&:nth-of-type(odd)": {
    paddingLeft: 0,
    borderRight: "1px solid #E6E4FF",
  },
  "&:nth-of-type(even)": {
    paddingRight: 0,
  },
  [theme.breakpoints.down("xs")]: {
    "&:nth-of-type(odd), &:nth-of-type(even)": {
      padding: 15,
      border: "none",
      flexBasis: "100%",
      flex: 1,
    },
  },
  "&.disabled": {
    color: "#999999",
    background: "#00000009",
    paddingLeft: 19,
  },
}));

export const SettingMetaData = styled("div")({
  display: "flex",
  flexFlow: "column",
  alignItems: "flex-start",
  justifyContent: "flex-start",
});

export const SettingTitleContainer = styled("div")({
  display: "flex",
  flexFlow: "row",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
});

export const SettingTitle = styled("div")({
  fontSize: "14px",
  lineHeight: "23px",
  fontWeight: 600,
  fontFamily: "inter",
  color: "#243443",
  ".disabled &": {
    color: "#999999",
  },
});

const LinkIconContainer = styled(IconButton)({
  position: "absolute",
  right: "-40px",
});

const SettingDescription = styled(SettingTitle)({
  fontSize: "12px",
  fontWeight: 400,
  padding: "11px",
  paddingBottom: 0,
  paddingLeft: 0,
});

const HighLight = styled("span")({
  color: "#5055cd",
  fontWeight: 700,
});

const BrowseInnerSettingsButton = styled(SettingsButton)({});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface KahunSettingPresentationProps {
  className?: string;
  setting: ConfigMetaData; //| StoredMetadata;
  onValueChanged: (ConfigMetaData, value) => void;
  valueWithSource: ValueWithSource;
  // Sub objects can be expanded instead of having their value changed
  onSubObjectExpand: (key: string) => void;
  // used for search to highlight the search term
  highlight?: string;
  disabled?: boolean;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]
const isLocalhost = process.env.REACT_APP_ENVIRONMENT === "local";

/***
 * Presets the setting with metadata and proper filed
 * (or, in case of subObject a brows button)
 */
export const KahunSettingPresentation = forwardRef<
  HTMLDivElement,
  KahunSettingPresentationProps
>(({ ...props }, ref) => {
  // Used for debug
  const [showRawMetadata, setShowRawMetadata] = useState(false);

  const [showLinkIcon, setShowLinkIcon] = useState(false);

  const highlightText = (stringToDisplay) => {
    if (!props.highlight || props.highlight === "") return stringToDisplay;
    const regEscape = (v) => v.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
    return stringToDisplay
      .split(new RegExp(regEscape(props.highlight), "ig"))
      .map((preText, i, arr) =>
        i === arr.length - 1 ? (
          preText
        ) : (
          <React.Fragment key={i}>
            {preText}
            <HighLight>
              {i === 0 && preText === "" // Capitalize in beginning of sentence
                ? props.highlight.toUpperCase().charAt(0)
                : props.highlight.charAt(0)}
              {props.highlight.slice(1)}
            </HighLight>
          </React.Fragment>
        ),
      );
  };

  return (
    <KahunSettingPresentationView
      ref={ref}
      className={clsx(
        props.className,
        "kahun-setting-presentation-view",
        props.disabled && "disabled",
      )}
      onMouseEnter={() => setShowLinkIcon(true)}
      onMouseLeave={() => setShowLinkIcon(false)}
    >
      <SettingMetaData>
        <SettingTitleContainer>
          <SettingTitle onClick={() => setShowRawMetadata(isLocalhost)}>
            {highlightText(props.setting.displayName)}
          </SettingTitle>
          <SettingSourceIcon valueWithSource={props.valueWithSource} />
          {showLinkIcon && (
            <LinkIconContainer
              onClick={() => props.onSubObjectExpand(props.setting.key)}
            >
              <LinkIcon />
            </LinkIconContainer>
          )}
        </SettingTitleContainer>
        {!!props.setting.description && props.setting.description != "" && (
          <SettingDescription>
            {highlightText(props.setting.description)}
          </SettingDescription>
        )}
      </SettingMetaData>
      {ConfigMetaData.isSubObject(props.setting) && (
        <BrowseInnerSettingsButton
          onClick={() => props.onSubObjectExpand(props.setting.key)}
          disabled={props.disabled}
        >
          Browse
        </BrowseInnerSettingsButton>
      )}
      {ConfigMetaData.isOption(props.setting) && (
        <KahunSettingValue
          settingMetaData={props.setting}
          value={props.valueWithSource?.value}
          onValueChange={props.onValueChanged}
          source={props.valueWithSource?.source}
          disabled={props.disabled}
        />
      )}
      {/* Debug only - works only in localhost */}
      <Dialog open={showRawMetadata} onClose={() => setShowRawMetadata(false)}>
        <DialogContent>
          <pre>
            <code>
              {JSON.stringify(
                {
                  valueWithSource: props.valueWithSource,
                  setting: props.setting,
                },
                null,
                4,
              )}
            </code>
          </pre>
        </DialogContent>
      </Dialog>
    </KahunSettingPresentationView>
  );
});
